import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Button, Div, Flex, GrowlNotification, Gutter, SearchInputWithScope } from '@gaiads/telia-react-component-library';
import { debounce } from 'lodash';
import { CustomPaginator, GenericTableProps, OrderList, OrderListTranslations } from '@cdaas/reusable-components-daas';
import { AdminProductOrder, EndUser, OrderType, PageMetadata, EndUserType } from '@cdaas/api-clients/dist/order-admin-openapi';

import OrderDetails from './OrderDetails';
import { orderAdminProductOrderClient } from '../../api-client/order-admin-client';

import styles from './CustomerOrders.module.scss';
import { assetAdminOrganizationClient } from '../../api-client/asset-admin-client';
import { productServiceAdminSalesChannelsClient } from '../../api-client/product-service-client';
import { resetOrderState, setOrderOrganization, setOrderSalesChannel } from '../../store/order';
import { AppDispatch } from '../../store';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Loader } from '../App/Loader';
import { clearSessionStorageOrderState, setStorageItems } from '../../utils';

const pageSize = 10;

enum SortableColumns {
  Created = 'created',
  Status = 'status',
}

interface Sort {
  column: SortableColumns;
  ascending: boolean;
}

enum SearchScope {
  OrderId = 'OrderId',
  ColtDeliveryId = 'ColtDeliveryId',
}

const CustomerOrders = () => {
  const { t } = useTranslation('customerOrders');
  const { t: tGlobal } = useTranslation('global');

  const getSearchScopeOption = (searchScope: SearchScope) => {
    if (searchScope === SearchScope.OrderId) {
      return { label: t('orderList.orderIdScope'), value: SearchScope.OrderId };
    }
    return { label: t('orderList.coltDeliveryIdScope'), value: SearchScope.ColtDeliveryId };
  };
  const [searchScope, setSearchScope] = useState<SearchScope>(SearchScope.OrderId);
  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState<GenericTableProps<AdminProductOrder>['loading']>('short');
  const [apiError, setApiError] = useState(false);
  const [orders, setOrders] = useState<AdminProductOrder[]>([]);
  const [ordersPageInfo, setOrdersPageInfo] = useState<PageMetadata | undefined>();

  const [currentSort, setCurrentSort] = useState<Sort | undefined>();

  const organizationId = useParams<{ id: string }>().id;

  const history = useHistory();
  const dispatch: AppDispatch = useDispatch();
  const [loadingOrderForOrganization, setLoadingOrderForOrganization] = useState<boolean>(false);
  const [initiateOrderForOrganizationError, setInitiateOrderForOrganizationError] = useState<boolean>(false);

  const { search } = useLocation();

  const fetchOrders = async (organizationId: string, pageSize: number, page: number, sort?: Sort, searchScope?: SearchScope, searchText?: string) => {
    setLoading('short');
    // Backend interprets e.g. '-status' as descending sorting on status and 'status' as ascending sorting on status
    const sorting = sort ? [`${sort?.ascending ? '' : '-'}${sort?.column}`] : undefined;
    try {
      const ordersResponse = (
        await orderAdminProductOrderClient.listProductOrder(
          undefined,
          undefined,
          undefined,
          [organizationId],
          undefined,
          undefined,
          searchScope === SearchScope.OrderId && searchText ? [searchText] : undefined,
          searchScope === SearchScope.ColtDeliveryId && searchText ? [searchText] : undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          OrderType.Default,
          sorting,
          page,
          pageSize,
        )
      ).data;

      if (ordersResponse.contents) {
        setOrders(ordersResponse.contents);
      }
      if (ordersResponse.page_metadata) {
        setOrdersPageInfo(ordersResponse.page_metadata);
      }
    } catch (error) {
      console.error(error);
      setApiError(true);
    }
    setLoading('none');
  };

  const handleSortChange = (sortColumn: SortableColumns) => {
    let newSort;
    if (sortColumn === currentSort?.column) {
      newSort = { column: currentSort.column, ascending: !currentSort.ascending };
    } else {
      newSort = { column: sortColumn, ascending: true };
    }
    setCurrentSort(newSort);
    fetchOrders(organizationId, pageSize, 0, newSort, searchScope, searchText);
  };

  const debouncedFetchOrders = useCallback(debounce(fetchOrders, 1000), []);

  const handleSearchChange = (searchTextInput: string) => {
    setSearchText(searchTextInput);
    setApiError(false);
    debouncedFetchOrders(organizationId, pageSize, 0, currentSort, searchScope, searchTextInput);
  };

  useEffect(() => {
    clearSessionStorageOrderState();
  }, []);

  useEffect(() => {
    const orderIdQuery = new URLSearchParams(search).get('order_id');
    if (orderIdQuery) {
      handleSearchChange(orderIdQuery);
    } else {
      fetchOrders(organizationId, pageSize, 0, undefined, undefined, undefined);
    }
  }, [organizationId]);

  const getSearchErrorText = () => {
    if (searchText.length !== 0 && orders.length === 0 && loading === 'none') {
      if (searchScope === SearchScope.OrderId) {
        return t('orderList.noOrdersFoundByOrderIdErrorText');
      } else if (searchScope === SearchScope.ColtDeliveryId) {
        return t('orderList.noOrdersFoundByColtDeliveryIdErrorText');
      }
    }
    return undefined;
  };

  const getEndUser = (endUser: EndUser): string => {
    switch (endUser.type) {
      case EndUserType.Daas:
        // Inactive user is set as fallback because every DaaS user should have email
        return !!endUser.given_name && !!endUser.family_name
          ? `${endUser.given_name} ${endUser.family_name}`
          : endUser.email || tGlobal('inactiveUser');
      case EndUserType.External:
        return endUser.user_id || tGlobal('companyDevice');
    }
  };

  const renderOrderDetails = (orderUniqueId: string) => {
    const order = orders.find(order => order.unique_id === orderUniqueId);
    return <OrderDetails organizationId={organizationId} order={order} />;
  };

  const handleInitiateOrderForOrganizationFlow = async () => {
    setLoadingOrderForOrganization(true);
    try {
      const organization = (await assetAdminOrganizationClient.adminOrganizationRead(organizationId)).data;
      const salesChannels = (await productServiceAdminSalesChannelsClient.getSalesChannels(organization.locale.country)).data.sales_channels;
      const salesChannel = salesChannels.find(sc => sc.uuid && sc.uuid === organization.sales_channel);

      if (!salesChannel) {
        throw new Error('Organization sales channel not found');
      }

      dispatch(resetOrderState()); // Clears user in case it has been set in any other part of the system
      dispatch(setOrderOrganization(organization));
      dispatch(setOrderSalesChannel(salesChannel));

      setStorageItems(['tSelectedOrganization', 'tSalesChannel', 'tOrderForOrganization'], [organization, salesChannel, true], 'session');

      history.push('/order');
    } catch {
      setInitiateOrderForOrganizationError(true);
      setLoadingOrderForOrganization(false);
    }
  };

  const translations: OrderListTranslations = {
    createdTimeHeader: t('orderList.createdTimeHeader'),
    orderedToHeader: t('orderList.orderedToHeader'),
    statusHeader: t('orderList.statusHeader'),
    noOrdersText: t('orderList.noOrdersText'),
    errorText: t('orderList.errorText'),
    statuses: {
      approved: t('orderList.statuses.approved'),
      delivered: t('orderList.statuses.delivered'),
      pendingApproval: t('orderList.statuses.pendingApproval'),
      financingDeclined: t('orderList.statuses.financingDeclined'),
      approvalDeclined: t('orderList.statuses.approvalDeclined'),
      cannotBeProcessed: t('orderList.statuses.cannotBeProcessed'),
      cancelled: t('orderList.statuses.cancelled'),
      returned: t('orderList.statuses.returned'),
      processingOrder: t('orderList.statuses.processingOrder'),
      discarded: t('orderList.statuses.discarded'),
      draft: t('orderList.statuses.draft'),
    },
  };

  if (loadingOrderForOrganization) {
    return <Loader />;
  }

  return (
    <>
      <Div className={styles['customer-orders__search-input-container']} style={{ width: '100%' }}>
        <Flex spaceBetween centeredVertically style={{ gap: '16px' }}>
          <SearchInputWithScope
            name="order-id-or-colt-delivery-id-search"
            showLabel={true}
            label={t('orderList.findByOrderIdOrByColtDeliveryIdLabel')}
            errorText={getSearchErrorText()}
            placeholder={t('orderList.searchPlaceholder')}
            helperText={searchScope === SearchScope.OrderId ? t('orderList.orderIdHelperText') : t('orderList.coltDeliveryIdHelperText')}
            searchValue={searchText}
            searchOptions={[]}
            onSearchChange={(_event, value) => handleSearchChange(value)}
            scopeOptions={Object.values(SearchScope).map(searchScope => getSearchScopeOption(searchScope))}
            selectedScope={getSearchScopeOption(searchScope)}
            onScopeChange={(_event, option) => {
              const scope = SearchScope[option.value as keyof typeof SearchScope];
              setSearchScope(scope);
              if (searchText) {
                fetchOrders(organizationId, pageSize, 0, currentSort, scope, searchText);
              }
            }}
          />
          <Button
            variant="secondary"
            style={{ width: '400px' }}
            onClick={handleInitiateOrderForOrganizationFlow}
            data-testid="order-for-organization-button"
          >
            {t('orderList.orderForOrganization')}
          </Button>
          {initiateOrderForOrganizationError && (
            <GrowlNotification
              notification={t('orderList.orderForOrganizationInitError')}
              variant={'error'}
              closeOnClick={() => setInitiateOrderForOrganizationError(false)}
            />
          )}
        </Flex>
      </Div>
      <Gutter size="lg" />
      <Div>
        <OrderList
          loading={loading}
          error={apiError}
          orders={orders.map(order => ({
            unique_id: order.unique_id,
            created: order.created || '',
            status: order.status,
            ordered_to_user: getEndUser(order.packages[0].end_user),
          }))}
          translations={translations}
          toggleCreatedTimeSort={() => handleSortChange(SortableColumns.Created)}
          toggleStatusSort={() => handleSortChange(SortableColumns.Status)}
          renderOrderListItemDetails={(orderUniqueId: string) => renderOrderDetails(orderUniqueId)}
        />
        {!!ordersPageInfo?.total_items && (
          <CustomPaginator
            activatePageInfoObject={ordersPageInfo}
            onPageChange={pageChangeInfo =>
              fetchOrders(organizationId, pageSize, pageChangeInfo.nextChosenPageNumber, currentSort, searchScope, searchText)
            }
            paginatorStyleProps={{
              pageSelectorProps: { centered: false },
            }}
          />
        )}
      </Div>
    </>
  );
};

export default CustomerOrders;
